import { useQuery } from '@tanstack/react-query'
import {
  BranchFormItem,
  ConfigFormItems,
  FunctionalityFormItem,
  VersionFormItem
} from './formItems'
import { connect } from 'react-redux'
import { useRouteMatch } from 'react-router-dom'
import { Form, Skeleton } from 'antd'
import './TaskForm.scss'

const TaskForm = ({ currentBranch, branches, form, plugins, taskId, onlyConfig = false }) => {

  taskId = useRouteMatch().params.taskId || taskId
  const functionalityFormItemValue = Form.useWatch('functionalityName', form)
  const functionalityVersionValue = Form.useWatch('functionalityVersion', form)
  const orchestrator = plugins['weaver-orchestrator']

  const getTask = async () => {
    const res = await orchestrator.getSchedule({ scheduleUid: taskId });
    return JSON.parse(res);
  }

  const getFunctionalities = async () => {
    const res = await orchestrator.getFunctionalities();
    return JSON.parse(res);
  }

  const functionalities = useQuery({
    queryKey: ['pipelines.functionalities'],
    queryFn: getFunctionalities,
  })

  const task = useQuery({
    queryKey: [`pipelines.tasks.${taskId}`],
    queryFn: getTask,
    enabled: (!functionalities.isLoading && taskId !== 'new'),
    onSuccess: (data) => fillDefaultValues(data),
  })

  const parseCascaderData = () => {
    const uniqFuncs = _.uniqBy(functionalities.data, 'name')
    const cascaderData = buildCascaderData(uniqFuncs.map(({ name }) => name))
    return cascaderData
  }

  const buildCascaderData = (uniqFuncs) => {
    let result = []
    uniqFuncs.forEach((f) =>
      f.split('.')
      .reduce((object, value) => {
        object.children = (object.children === undefined) ? [] : object.children
        let item = object.children.find((child) => child.value === value)
        !item ? object.children.push(item = { value, label: _.startCase(value) }) : null
        return item
      }
      , { children: result })
    )
    return result
  }

  const getVersionData = () => {
    const validItems = functionalities.data.filter((f) => f.name === functionalityFormItemValue.join('.'))
    return validItems.map(({ version }) => version)
  }

  const fillDefaultValues = (data) => {
    const { branch, config, functionalityName, functionalityVersion } = data
    const functionality = functionalities.data.find((f) => f.name === functionalityName)

    const properties = functionality.configDefinition.properties
    const configDefTypesMap = {}
    for (const key in properties) {
      configDefTypesMap[key] = {
        type: properties[key].type,
        subType: properties[key].subType
      }
    }

    const fields = {
      functionalityName: functionalityName.split('.'),
      functionalityVersion,
      branch,
      config,
      configDefTypesMap
    }
    form.setFieldsValue(fields)
  }

  const getFunctionality = (value, version) => {
    return value ? functionalities.data.find(func => func.name === value.join('.') && func.version === version) : undefined
  }

  const renderDescription = () => {

    return (
      <Form.Item
        noStyle
        shouldUpdate={(prevValues, currentValues) =>
          prevValues.functionalityName !== currentValues.functionalityName
        }
      >
        {() =>
          functionalityFormItemValue !== undefined && (
            <Form.Item label="Description">
              { getFunctionality(functionalityFormItemValue, functionalityVersionValue)?.configDefinition?.description }
            </Form.Item>
          )
        }
      </Form.Item>
    )
  }

  const renderVersion = () => {
    return (
      <Form.Item
        noStyle
        shouldUpdate={(prevValues, currentValues) =>
          prevValues.functionalityName !== currentValues.functionalityName
        }
      >
      {() =>
        functionalityFormItemValue && (
          <VersionFormItem
            taskId={taskId}
            versionData={getVersionData()}
          />
        )
      }
    </Form.Item>
    )
  }

  if (taskId === 'new') {
    const fields = {
      branch: currentBranch || 'main'
    }
    form.setFieldsValue(fields)
  }

  const renderFunctionalityAndBranch = () => {
    return(
      <>
        { renderDescription() }
        { renderVersion() }
        <BranchFormItem
          branches={branches}
          taskId={taskId}
        />
      </>
    )
  }

  return (
    <Skeleton
      active
      loading={taskId !== 'new' ? task.isLoading : false}>
      <Form
        className="create-task-form"
        form={form}
        name="taskForm"
      >
        <FunctionalityFormItem
          form={form}
          cascaderData={functionalities.isLoading ? [] : parseCascaderData()}
          taskId={taskId}
        />
        { !onlyConfig ? renderFunctionalityAndBranch() : null }
        <ConfigFormItems
          form={form}
          functionality={getFunctionality(functionalityFormItemValue, functionalityVersionValue)}
        />
      </Form>
    </Skeleton>
  )
}

export default connect(({ branches, explore }) => (
  {
    plugins: explore.plugins,
    branches: branches.branches,
    currentBranch: branches.currentBranch
  })
)(TaskForm)
